import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./client";
import type {
  RunDetail, RunSummary, ScriptInfo, StartRunRequest, SystemInfo, Topology,
} from "./types";
import type { GradientStatus, GradientTelemetry } from "./gradient";

export function useSystem() {
  return useQuery({
    queryKey: ["system"],
    queryFn: () => api.get<SystemInfo>("/api/v1/system"),
    refetchInterval: 3000, // live GPU fleet
  });
}

export function useRuns() {
  return useQuery({
    queryKey: ["runs"],
    queryFn: () => api.get<RunSummary[]>("/api/v1/runs"),
    refetchInterval: 5000,
  });
}

export function useScripts() {
  return useQuery({
    queryKey: ["scripts"],
    queryFn: () => api.get<ScriptInfo[]>("/api/v1/scripts"),
  });
}

export function useTopology() {
  return useQuery({
    queryKey: ["topology"],
    queryFn: () => api.get<Topology>("/api/v1/topology"),
    staleTime: Infinity, // architecture descriptors don't change within a session
  });
}

export function useRunDetail(name: string, live: boolean) {
  return useQuery({
    queryKey: ["run", name],
    queryFn: () => api.get<RunDetail>(`/api/v1/runs/${encodeURIComponent(name)}`),
    enabled: !!name,
    refetchInterval: live ? 4000 : false,
  });
}

export function useLaunchRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: StartRunRequest) =>
      api.post<{ name: string; status: string; gpu_id: number }>("/api/v1/runs", req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });
}

function runAction<T = unknown>(verb: (n: string) => Promise<T>) {
  return function useRunAction() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: verb,
      onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
    });
  };
}

const enc = encodeURIComponent;
export const useStopRun = runAction((n: string) => api.post(`/api/v1/runs/${enc(n)}/stop`));
export const useRestartRun = runAction((n) => api.post<{ name: string }>(`/api/v1/runs/${enc(n)}/restart`));
export const useDeleteRun = runAction((n) => api.del(`/api/v1/runs/${enc(n)}`));
export const useCompactRun = runAction((n) => api.post<{ freed: number }>(`/api/v1/runs/${enc(n)}/compact`));

// ── Gradient training ─────────────────────────────────────────────────────
// Cheap liveness poll — drives the "gradient live" banner on the dashboard and
// gates the heavier telemetry fetch.
export function useGradientStatus() {
  return useQuery({
    queryKey: ["gradientStatus"],
    queryFn: () => api.get<GradientStatus>("/api/v1/gradient/status"),
    refetchInterval: 2000,
  });
}

// The full telemetry blob. Only polled while `enabled` (i.e. telemetry present).
// A 404 (no run yet / already cleared) resolves to null so the view shows its
// waiting state instead of erroring.
export function useGradient(enabled: boolean) {
  return useQuery({
    queryKey: ["gradient"],
    queryFn: async (): Promise<GradientTelemetry | null> => {
      try {
        return await api.get<GradientTelemetry>("/api/v1/gradient");
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled,
    refetchInterval: enabled ? 1200 : false,
  });
}

export function useExtendRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, generations }: { name: string; generations: number }) =>
      api.post<{ name: string }>(`/api/v1/runs/${enc(name)}/extend`, { generations }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });
}
